import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, Sheet, Typography } from "@mui/joy";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useUpdateRowMutation,
  useViewDataQuery,
} from "../../../hooks/useViewDataQuery";
import { useAccumulatedViewRows } from "../../../hooks/useAccumulatedViewRows";
import { useWorkspaceContext } from "../../../../../providers/WorkspaceProvider";
import type { CanvasDataSource } from "../../../types";
import type { CanvasWriteContext } from "../../../services/viewService";
import type { RenderableElement } from "../CanvasRenderer";
import { useCanvasRuntime } from "../CanvasRuntimeContext";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { openCanvasNavigation } from "../canvasNavigation";

/** Rows loaded automatically before the board asks the user to load more. */
export const KANBAN_AUTO_LOAD_ROWS = 1000;
const OTHER_LANE = "__other__";

export interface KanbanConfig {
  status_field?: string;
  columns?: string[];
  title_field?: string;
  subtitle_field?: string;
  card_click_action?: {
    type?: string;
    config?: Record<string, unknown>;
  } | null;
}

interface Props {
  element: RenderableElement;
  dataSources: CanvasDataSource[];
  canvasId?: string | null;
}

const display = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

export function CanvasKanbanRenderer({
  element,
  dataSources,
  canvasId,
}: Props) {
  const outer = element.props as unknown as KanbanConfig & {
    title?: string;
    data_source_alias?: string;
    kanban_config?: KanbanConfig;
  };
  const config: KanbanConfig = { ...outer, ...outer.kanban_config };
  const runtime = useCanvasRuntime();
  const navigate = useNavigate();
  const location = useLocation();
  const authFetch = useAuthFetch();
  const effectiveCanvasId = canvasId ?? runtime?.canvasId ?? null;
  const alias = outer.data_source_alias || "main";
  const source = dataSources.find((candidate) =>
    [
      candidate.id,
      candidate.name,
      (candidate as { alias?: string }).alias,
    ].includes(alias),
  );
  const viewId = source?.view_id ? String(source.view_id) : null;
  const columns = useMemo(
    () => (Array.isArray(config.columns) ? config.columns : []),
    [config.columns],
  );
  const configured = Boolean(
    effectiveCanvasId &&
    viewId &&
    config.status_field &&
    config.title_field &&
    columns.length > 0,
  );

  const relationshipFilters = useMemo(
    () =>
      runtime?.interactionBus
        .getRuntimeFilters({
          canvasId: effectiveCanvasId,
          elementId: element.id,
          dataSourceAlias: alias,
        })
        .flatMap((entry) => entry.filters) || [],
    [alias, effectiveCanvasId, element.id, runtime],
  );
  const {
    rows,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  } = useAccumulatedViewRows(configured ? viewId : null, {
    canvasId: effectiveCanvasId || undefined,
    filters: relationshipFilters.length ? relationshipFilters : undefined,
    filterLogic: "AND",
  });
  // Load every page up to a ceiling, then ask: a board must not silently drop cards.
  useEffect(() => {
    if (hasMore && !isLoadingMore && rows.length < KANBAN_AUTO_LOAD_ROWS) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore, rows.length]);

  const writable = element.effectiveAccess !== "read";
  const updateRow = useUpdateRowMutation();
  const writeContext = useMemo<CanvasWriteContext | undefined>(
    () =>
      effectiveCanvasId
        ? {
            canvasId: effectiveCanvasId,
            elementId: element.id,
            embeddingPath: runtime?.embeddingPath,
          }
        : undefined,
    [effectiveCanvasId, element.id, runtime?.embeddingPath],
  );

  // Optimistic moves, in-flight moves and the move error belong to ONE context: workspace,
  // canvas, view, filters and status field. A move made in another context is never shown here,
  // and a callback from an old context cannot change this one.
  const { workspace } = useWorkspaceContext();
  const scope = JSON.stringify([
    workspace?.id ?? null,
    effectiveCanvasId,
    viewId,
    relationshipFilters,
    config.status_field ?? null,
  ]);
  const [board, setBoard] = useState<{
    scope: string;
    moves: Record<string, string>;
    /** The request id of each card's in-flight move. */
    pending: Record<string, number>;
    error: string | null;
  }>({ scope, moves: {}, pending: {}, error: null });
  const nextRequestId = useRef(0);
  // Leaving a context discards its state, so coming back to it later cannot revive the old
  // in-flight moves: only the request that made an entry can settle it.
  useEffect(() => {
    setBoard((latest) =>
      latest.scope === scope
        ? latest
        : { scope, moves: {}, pending: {}, error: null },
    );
  }, [scope]);
  const current =
    board.scope === scope
      ? board
      : { scope, moves: {}, pending: {}, error: null };
  const { moves, pending } = current;
  const moveError = current.error;
  const [navigationError, setNavigationError] = useState<string | null>(null);

  const statusOf = useCallback(
    (row: Record<string, unknown>): string =>
      config.status_field
        ? (moves[String(row._rowId)] ??
          display(row[config.status_field]).trim())
        : "",
    [config.status_field, moves],
  );

  const lanes = useMemo(() => {
    const byLane = new Map<string, Array<Record<string, unknown>>>(
      columns.map((column) => [column, []]),
    );
    const other: Array<Record<string, unknown>> = [];
    for (const row of rows as Array<Record<string, unknown>>) {
      const lane = byLane.get(statusOf(row));
      if (lane) lane.push(row);
      else other.push(row);
    }
    const result = columns.map((column) => ({
      id: column,
      label: column,
      cards: byLane.get(column) || [],
    }));
    if (other.length > 0) {
      result.push({ id: OTHER_LANE, label: "Other", cards: other });
    }
    return result;
  }, [columns, rows, statusOf]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source: from, draggableId } = result;
      if (!writable || !viewId || !config.status_field) return;
      if (!destination || destination.droppableId === from.droppableId) return;
      if (destination.droppableId === OTHER_LANE) return;
      // one move per card at a time: a second drop while the first is in flight is refused
      if (pending[draggableId]) return;
      const status = destination.droppableId;
      const droppedIn = scope;
      const requestId = (nextRequestId.current += 1);
      const previous = moves[draggableId];
      setBoard({
        scope,
        moves: { ...moves, [draggableId]: status },
        pending: { ...pending, [draggableId]: requestId },
        error: null,
      });
      const settle = (revertTo: "keep" | "previous", error: string | null) =>
        setBoard((latest) => {
          // only this request may settle its own entry, in the context it was made in
          if (
            latest.scope !== droppedIn ||
            latest.pending[draggableId] !== requestId
          ) {
            return latest;
          }
          const nextPending = { ...latest.pending };
          delete nextPending[draggableId];
          const nextMoves = { ...latest.moves };
          if (revertTo === "previous") {
            if (previous === undefined) delete nextMoves[draggableId];
            else nextMoves[draggableId] = previous;
          }
          return {
            ...latest,
            moves: nextMoves,
            pending: nextPending,
            error: error ?? latest.error,
          };
        });
      void updateRow
        .mutateAsync({
          viewId,
          rowId: draggableId,
          data: { [config.status_field]: status },
          context: writeContext,
        })
        .then(() => settle("keep", null))
        .catch((failure: unknown) =>
          settle(
            "previous",
            failure instanceof Error ? failure.message : "Please try again.",
          ),
        );
    },
    [
      config.status_field,
      moves,
      pending,
      scope,
      updateRow,
      viewId,
      writable,
      writeContext,
    ],
  );

  const openCard = useCallback(
    (rowId: string) => {
      const action = config.card_click_action;
      if (!action || action.type !== "NAVIGATE" || !runtime) return;
      setNavigationError(null);
      void openCanvasNavigation(action.config as never, {
        workspaceId: runtime.workspaceId,
        currentRowId: runtime.rowId,
        selectedRowId: rowId,
        returnPath: location.pathname + location.search,
        authFetch,
        navigate,
      }).catch((failure: unknown) => {
        setNavigationError(
          failure instanceof Error
            ? failure.message
            : "Destination unavailable or access denied.",
        );
      });
    },
    [
      authFetch,
      config.card_click_action,
      location.pathname,
      location.search,
      navigate,
      runtime,
    ],
  );
  const clickable = config.card_click_action?.type === "NAVIGATE" && !!runtime;

  return (
    <Sheet
      variant="outlined"
      sx={{ p: 2, borderRadius: "sm", height: "100%", overflow: "auto" }}
    >
      {outer.title && (
        <Typography level="title-md" sx={{ mb: 2 }}>
          {outer.title}
        </Typography>
      )}
      {!configured ? (
        <Typography level="body-sm">
          Configure a view, status field, columns and title field for this
          board.
        </Typography>
      ) : error ? (
        <Typography role="alert" level="body-sm">
          Unable to load board.
        </Typography>
      ) : isLoading ? (
        <Typography level="body-sm">Loading board…</Typography>
      ) : (
        <>
          {moveError && (
            <Typography
              role="alert"
              level="body-sm"
              color="danger"
              sx={{ mb: 1 }}
            >
              Could not move the card: {moveError}
            </Typography>
          )}
          {navigationError && (
            <Typography
              role="alert"
              level="body-sm"
              color="danger"
              sx={{ mb: 1 }}
            >
              {navigationError}
            </Typography>
          )}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              {lanes.map((lane) => (
                <Box
                  key={lane.id}
                  sx={{ minWidth: 220, flex: "1 1 220px", maxWidth: 360 }}
                >
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    {`${lane.label} (${lane.cards.length})`}
                  </Typography>
                  <Droppable
                    droppableId={lane.id}
                    isDropDisabled={!writable || lane.id === OTHER_LANE}
                  >
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        data-testid={`lane-${lane.id}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          minHeight: 64,
                          p: 1,
                          borderRadius: "sm",
                          bgcolor: "background.level1",
                        }}
                      >
                        {lane.cards.length === 0 && (
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>
                            No cards
                          </Typography>
                        )}
                        {lane.cards.map((card, index) => {
                          const rowId = String(card._rowId);
                          return (
                            <Draggable
                              key={rowId}
                              draggableId={rowId}
                              index={index}
                              isDragDisabled={!writable || !!pending[rowId]}
                            >
                              {(dragProvided) => (
                                <Sheet
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  variant="outlined"
                                  onClick={
                                    clickable
                                      ? () => openCard(rowId)
                                      : undefined
                                  }
                                  sx={{
                                    p: 1,
                                    borderRadius: "sm",
                                    cursor: clickable
                                      ? "pointer"
                                      : writable
                                        ? "grab"
                                        : "default",
                                  }}
                                >
                                  <Typography level="title-sm">
                                    {display(card[config.title_field!])}
                                  </Typography>
                                  {config.subtitle_field &&
                                    display(card[config.subtitle_field]) && (
                                      <Typography level="body-xs">
                                        {display(card[config.subtitle_field])}
                                      </Typography>
                                    )}
                                </Sheet>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              ))}
            </Box>
          </DragDropContext>
          {hasMore && totalCount !== undefined && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <Typography level="body-sm">
                Showing {rows.length} of {totalCount} cards
              </Typography>
              <Button
                size="sm"
                variant="outlined"
                loading={isLoadingMore}
                onClick={loadMore}
              >
                Load more
              </Button>
            </Box>
          )}
        </>
      )}
    </Sheet>
  );
}

export default CanvasKanbanRenderer;
